/**
 * Image Generation Service using Gemini 2.0 Flash
 *
 * Uses Gemini's native image generation capabilities.
 * Uses the same REACT_APP_GEMINI_API_KEY.
 */

import { GoogleGenerativeAI } from '@google/generative-ai';

const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

/**
 * Build a simple prompt for clean scientific images
 * @param {string} basePrompt - The base description
 * @returns {string} - Simple prompt
 */
const buildEnhancedPrompt = (basePrompt) => {
  return `${basePrompt}

Style: Simple, clean diagram. Large readable labels. White background. Minimal detail.`;
};

/**
 * Generate an image using Gemini 2.0 Flash
 * @param {string} prompt - Description of the image to generate
 * @returns {Promise<string>} - Base64 data URL of the generated image
 */
export const generateImage = async (prompt) => {
  if (!GEMINI_API_KEY) {
    throw new Error('REACT_APP_GEMINI_API_KEY is not configured. Please add it to your .env file.');
  }

  try {
    // Sanitize and enhance prompt
    const sanitizedPrompt = prompt
      .replace(/[\x00-\x1F\x7F]/g, ' ')  // Remove control characters
      .replace(/\s+/g, ' ')              // Normalize whitespace
      .trim();

    const enhancedPrompt = buildEnhancedPrompt(sanitizedPrompt);

    const genAI = new GoogleGenerativeAI(GEMINI_API_KEY);

    const model = genAI.getGenerativeModel({
      model: 'gemini-2.0-flash-exp-image-generation',
      generationConfig: {
        responseModalities: ['image', 'text'],
      },
    });

    const result = await model.generateContent(enhancedPrompt);
    const response = result.response;

    // Extract image from response parts
    const candidates = response.candidates;
    if (candidates && candidates.length > 0) {
      const parts = candidates[0].content?.parts || [];

      for (const part of parts) {
        if (part.inlineData) {
          const { mimeType, data } = part.inlineData;
          // Return as data URL for direct use in img src
          return `data:${mimeType};base64,${data}`;
        }
      }
    }

    throw new Error('No image was generated in the response');
  } catch (error) {
    console.error('Image generation error:', error);
    throw error;
  }
};

/**
 * Generate a scientific diagram for MCAT/DAT passages
 * @param {string} topic - The scientific topic (e.g., "cell mitosis", "enzyme kinetics")
 * @param {string} type - Type of diagram (e.g., "diagram", "chart", "graph")
 * @returns {Promise<string>} - Base64 data URL of the generated image
 */
export const generateScientificDiagram = async (topic, type = 'diagram') => {
  const prompt = `Create a scientific ${type} of ${topic}. Educational illustration, clean and clear, labeled parts, textbook style, white background, high detail, professional medical/scientific illustration suitable for a standardized exam.`;

  return generateImage(prompt);
};

/**
 * Generate a data table/chart visualization
 * @param {string} description - Description of what the table/chart should show
 * @returns {Promise<string>} - Base64 data URL of the generated image
 */
export const generateDataVisualization = async (description) => {
  const prompt = `Create a scientific data visualization showing ${description}. Clean chart or table, clear labels, professional style, white background, suitable for an academic paper or standardized exam.`;

  return generateImage(prompt);
};

/**
 * Check if image generation is available (API key configured)
 * @returns {boolean}
 */
export const isImageGenerationAvailable = () => {
  return !!GEMINI_API_KEY;
};
